import dataApiFactory from '../dataApiFactory'
import RestDataService from '@yakit/core/api/RestDataService'
import { extend } from '@yakit/core'
import type { Customer } from '~/types/entities/Customer'

interface FamilyMember {
  id: string
  child: Customer
  parent: Customer
}

interface Ancestors {
  parent?: Customer
  grandParent?: Customer
}

interface FamilyApi {
  search: (params: { relativesOfId?: string; listType?: string; q?: { childId: string } }) => Promise<{ data: FamilyMember[] }>
}

interface CustomerApi extends ReturnType<typeof RestDataService> {
  listFamily: (id: string, listType: string) => Promise<Customer[]>
  getParent: (childId: string) => Promise<Customer | undefined>
  getAncestors: (childId: string) => Promise<Ancestors>
  setPrimaryContact: (customerId: string, contactId: string) => Promise<Customer>
}

/**
 * Customer Rest Api additions
 */
export default function(opts: Parameters<typeof RestDataService>[0]): CustomerApi {
  const familyApi = dataApiFactory.ar.custFamily as FamilyApi
  const api = RestDataService(opts)

  return extend(api, {
    listFamily: async (id: string, listType: string): Promise<Customer[]> => {
      const familyList = await familyApi.search({ relativesOfId: id, listType })
      return familyList.data.map(family => family.child)
    },

    getParent: async (childId: string): Promise<Customer | undefined> => {
      const familyList = await familyApi.search({ q: { childId } })
      return familyList.data[0]?.parent
    },

    getAncestors: async (childId: string): Promise<Ancestors> => {
      const parent = await (api as CustomerApi).getParent(childId)
      let grandParent: Customer | undefined
      if (parent) {
        grandParent = await (api as CustomerApi).getParent(parent.id)
      }
      return { parent, grandParent }
    },

    setPrimaryContact: async (customerId: string, contactId: string): Promise<Customer> => {
      const result = await api.update({ id: customerId, org: { contact: { id: contactId } } })
      return result
    },
  }) as CustomerApi
} 